import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { FEATURES, hasPermission, ROLES } from "../lib/rolesConfig";
import {
  commitFirestoreWrite,
  isOffline,
  offlineAwareMessage,
} from "../utils/offline";
import {
  FaChevronLeft,
  FaChevronRight,
  FaEllipsisH,
  FaEdit,
  FaExclamationTriangle,
  FaHeartbeat,
  FaPills,
  FaStickyNote,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

const REQUIRED_VITAL_FIELDS = [
  { id: "bloodPressure", label: "Blood Pressure", type: "bp", unit: "mmHg" },
  { id: "bloodSugar", label: "Blood Sugar", type: "number", unit: "mg/dL" },
  { id: "heartRate", label: "Pulse Rate", type: "number", unit: "bpm" },
  { id: "o2Sat", label: "O2 Saturation", type: "number", unit: "%" },
  { id: "temperature", label: "Temperature", type: "number", unit: "C" },
  { id: "weight", label: "Weight", type: "number", unit: "kg" },
];

const toDateKeyLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTodayDate = () => toDateKeyLocal(new Date());
const getCurrentTime = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes(),
  ).padStart(2, "0")}`;
};

const withDateShift = (dateKey, deltaDays) => {
  const date = new Date(`${dateKey}T00:00:00`);
  date.setDate(date.getDate() + deltaDays);
  return toDateKeyLocal(date);
};

const toDisplayDate = (dateKey) =>
  new Date(`${dateKey}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const toDisplayTime = (value) => {
  if (!value) return "";
  const [h, m] = value.split(":");
  const date = new Date();
  date.setHours(Number(h || 0), Number(m || 0), 0, 0);
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const timestampFromDateAndTime = (dateKey, timeValue) =>
  Timestamp.fromDate(new Date(`${dateKey}T${timeValue}:00`));

const isBlank = (value) => String(value || "").trim() === "";
const makeSegmentId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const MEDICATION_OUTCOMES = {
  GIVEN: "Given",
  HELD: "Held",
  SKIPPED: "Skipped",
};

const MEDICATION_HOLD_REASONS = [
  "BP below threshold",
  "Patient refused",
  "Not available",
  "Other",
];

const getMedicationOutcome = (outcomes, medicationId) => ({
  status: MEDICATION_OUTCOMES.GIVEN,
  reason: "",
  ...(outcomes?.[medicationId] || {}),
});

const isMedicationGiven = (outcome) =>
  (outcome?.status || MEDICATION_OUTCOMES.GIVEN) === MEDICATION_OUTCOMES.GIVEN;

const compactVitals = (vitalsDraft) => {
  const next = {};
  Object.entries(vitalsDraft).forEach(([key, value]) => {
    if (key === "bloodPressure") {
      const systolic = String(value?.systolic || "").trim();
      const diastolic = String(value?.diastolic || "").trim();
      if (systolic || diastolic) next[key] = { systolic, diastolic };
      return;
    }
    const raw = String(value || "").trim();
    if (raw) next[key] = raw;
  });
  return next;
};

const toInventoryNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const blurActiveField = () => {
  if (typeof document === "undefined") return;
  const activeElement = document.activeElement;
  if (activeElement && typeof activeElement.blur === "function") {
    activeElement.blur();
  }
};

const DailyCareLogPage = () => {
  const { currentUser, activeCareProfile, userRole } = useAuth();
  const editorRef = useRef(null);
  const menuContainerRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [entryTime, setEntryTime] = useState(getCurrentTime());
  const [entries, setEntries] = useState([]);
  const [trashedEntries, setTrashedEntries] = useState([]);
  const [viewMode, setViewMode] = useState("active");
  const [openMenuEntryId, setOpenMenuEntryId] = useState(null);
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [enabledVitals, setEnabledVitals] = useState(REQUIRED_VITAL_FIELDS);
  const [draftSegments, setDraftSegments] = useState([]);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [undoState, setUndoState] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [editingSegmentId, setEditingSegmentId] = useState(null);

  const [vitalsDraft, setVitalsDraft] = useState({});
  const [vitalsNoteDraft, setVitalsNoteDraft] = useState("");
  const [medDraft, setMedDraft] = useState({
    medicationIds: [],
    medicationDosages: {},
    medicationOutcomes: {},
    note: "",
  });
  const [showAddMedicationInline, setShowAddMedicationInline] = useState(false);
  const [newMedicationName, setNewMedicationName] = useState("");
  const [isCreatingDraftMedication, setIsCreatingDraftMedication] =
    useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [incidentDraft, setIncidentDraft] = useState({
    type: "Fall",
    description: "",
    actionTaken: "",
  });

  const isOwner = activeCareProfile?.userId === currentUser?.uid;
  const role = isOwner ? ROLES.OWNER : userRole?.[activeCareProfile?.id];
  const canRead = hasPermission(role, FEATURES.DAILY_CARE_LOG, "read");
  const canWrite = hasPermission(role, FEATURES.DAILY_CARE_LOG, "write");

  const medicationMap = useMemo(() => {
    const map = {};
    medicationOptions.forEach((item) => {
      map[item.id] = item;
    });
    return map;
  }, [medicationOptions]);

  useEffect(() => {
    if (!activeCareProfile?.id) {
      setEntries([]);
      setTrashedEntries([]);
      return;
    }
    const q = query(
      collection(db, "dailyCareLogs"),
      where("careProfileId", "==", activeCareProfile.id),
      where("date", "==", selectedDate),
      orderBy("occurredAt", "asc"),
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const allEntries = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setEntries(allEntries.filter((entry) => !entry.isTrashed));
        setTrashedEntries(
          allEntries.filter((entry) => Boolean(entry.isTrashed)),
        );
      },
      (error) => console.error("Failed to load daily care logs", error),
    );
    return () => unsubscribe();
  }, [activeCareProfile, selectedDate]);

  useEffect(() => {
    if (!activeCareProfile?.id) {
      setMedicationOptions([]);
      return;
    }
    const q = query(
      collection(db, "medications"),
      where("careProfileId", "==", activeCareProfile.id),
      where("status", "in", ["Active", "PRN", "Draft"]),
      orderBy("medicationName", "asc"),
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setMedicationOptions(
          snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
        );
      },
      (error) => console.error("Failed to load medication options", error),
    );
    return () => unsubscribe();
  }, [activeCareProfile]);

  useEffect(() => {
    const fetchVitalsConfig = async () => {
      if (!activeCareProfile?.id) {
        setEnabledVitals(REQUIRED_VITAL_FIELDS);
        return;
      }
      try {
        const profileDoc = await getDoc(
          doc(db, "careProfiles", activeCareProfile.id),
        );
        if (!profileDoc.exists()) {
          setEnabledVitals(REQUIRED_VITAL_FIELDS);
          return;
        }
        const config = profileDoc.data().vitalsConfig || [];
        if (config.length === 0) {
          setEnabledVitals(REQUIRED_VITAL_FIELDS);
          return;
        }

        const requiredFieldMap = REQUIRED_VITAL_FIELDS.reduce((acc, field) => {
          acc[field.id] = field;
          return acc;
        }, {});

        const enabledFromConfig = config
          .filter((item) => item.enabled && !item.archived)
          .map((item) => {
            const base = requiredFieldMap[item.id] || {};
            return {
              id: item.id,
              label: item.name || base.label || item.id,
              unit: item.unit || base.unit || "",
              type:
                item.type === "systolic/diastolic" || base.type === "bp"
                  ? "bp"
                  : "number",
            };
          });

        // Respect Manage Vitals Fields exactly (including intentionally all-off).
        setEnabledVitals(enabledFromConfig);
      } catch (error) {
        console.error("Failed to load vitals config", error);
        setEnabledVitals(REQUIRED_VITAL_FIELDS);
      }
    };
    fetchVitalsConfig();
  }, [activeCareProfile]);

  useEffect(() => {
    if (!undoState) return undefined;
    const timeout = setTimeout(() => setUndoState(null), 4000);
    return () => clearTimeout(timeout);
  }, [undoState]);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const timeout = setTimeout(() => setToastMessage(""), 2500);
    return () => clearTimeout(timeout);
  }, [toastMessage]);

  useEffect(() => {
    if (!openMenuEntryId) return undefined;
    const handleOutsideClick = (event) => {
      if (
        menuContainerRef.current &&
        !menuContainerRef.current.contains(event.target)
      ) {
        setOpenMenuEntryId(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [openMenuEntryId]);

  const resetDraft = () => {
    setEntryTime(getCurrentTime());
    setDraftSegments([]);
    setEditingEntryId(null);
    setSaveFeedback("");
    setUndoState(null);
    setActiveModal(null);
    setEditingSegmentId(null);
  };

  const openModal = (type, segment = null) => {
    const isEditingExistingSegment = Boolean(segment);
    if (!isEditingExistingSegment) {
      setEditingEntryId(null);
      setDraftSegments([]);
      setEntryTime(getCurrentTime());
      setUndoState(null);
    }
    setEditingSegmentId(segment?.id || null);
    if (type === "vitals") {
      const segmentData = segment?.data || {};
      const { note, ...vitalsValues } = segmentData;
      setVitalsDraft(vitalsValues);
      setVitalsNoteDraft(note || "");
    }
    if (type === "meds") {
      setMedDraft(
        segment?.data || {
          medicationIds: [],
          medicationDosages: {},
          medicationOutcomes: {},
          note: "",
        },
      );
      setShowAddMedicationInline(false);
      setNewMedicationName("");
    }
    if (type === "note") {
      setNoteDraft(segment?.data?.text || "");
    }
    if (type === "incident") {
      setIncidentDraft(
        segment?.data || {
          type: "Fall",
          description: "",
          actionTaken: "",
        },
      );
    }
    setActiveModal(type);
  };

  const closeModal = () => {
    blurActiveField();
    if (editingEntryId) {
      resetDraft();
      return;
    }
    setActiveModal(null);
    setEditingSegmentId(null);
    setTimeout(() => editorRef.current?.focus(), 0);
  };

  const upsertSegment = async (segment) => {
    const currentSegments = Array.isArray(draftSegments) ? draftSegments : [];
    const index = currentSegments.findIndex(
      (item) => item.id === editingSegmentId,
    );
    const nextSegments =
      index >= 0
        ? currentSegments.map((item) =>
            item.id === editingSegmentId
              ? { ...segment, id: editingSegmentId }
              : item,
          )
        : [...currentSegments, { ...segment, id: makeSegmentId() }];
    await persistEntry(nextSegments);
  };

  const removeSegment = async (segmentId) => {
    const nextSegments = (draftSegments || []).filter(
      (item) => item.id !== segmentId,
    );
    await persistEntry(nextSegments);
  };

  const undoLastInsert = () => {
    if (!undoState?.id) return;
    removeSegment(undoState.id);
    setUndoState(null);
    editorRef.current?.focus();
  };

  const saveVitalsSegment = async () => {
    if (enabledVitals.length === 0) {
      window.alert(
        "No vitals fields are enabled. Please turn on fields in Manage Vitals Fields first.",
      );
      return;
    }
    const vitals = compactVitals(vitalsDraft);
    if (Object.keys(vitals).length === 0) {
      window.alert("Enter at least one vital value before inserting.");
      return;
    }
    blurActiveField();
    await upsertSegment({
      type: "vitals",
      data: {
        ...vitals,
        note: String(vitalsNoteDraft || "").trim(),
      },
    });
  };

  const toggleMedSelection = (medicationId) => {
    setMedDraft((prev) => {
      const isSelected = prev.medicationIds.includes(medicationId);
      if (isSelected) {
        const nextDosages = { ...(prev.medicationDosages || {}) };
        const nextOutcomes = { ...(prev.medicationOutcomes || {}) };
        delete nextDosages[medicationId];
        delete nextOutcomes[medicationId];
        return {
          ...prev,
          medicationIds: prev.medicationIds.filter((id) => id !== medicationId),
          medicationDosages: nextDosages,
          medicationOutcomes: nextOutcomes,
        };
      }
      const medication = medicationMap[medicationId];
      return {
        ...prev,
        medicationIds: [...prev.medicationIds, medicationId],
        medicationDosages: {
          ...(prev.medicationDosages || {}),
          [medicationId]:
            prev.medicationDosages?.[medicationId] ||
            medication?.strength ||
            medication?.dosage ||
            "",
        },
        medicationOutcomes: {
          ...(prev.medicationOutcomes || {}),
          [medicationId]: getMedicationOutcome(
            prev.medicationOutcomes,
            medicationId,
          ),
        },
      };
    });
  };

  const handleMedDosageChange = (medicationId, value) => {
    setMedDraft((prev) => ({
      ...prev,
      medicationDosages: {
        ...(prev.medicationDosages || {}),
        [medicationId]: value,
      },
    }));
  };

  const handleMedicationOutcomeChange = (medicationId, status) => {
    setMedDraft((prev) => ({
      ...prev,
      medicationOutcomes: {
        ...(prev.medicationOutcomes || {}),
        [medicationId]: {
          ...getMedicationOutcome(prev.medicationOutcomes, medicationId),
          status,
          reason:
            status === MEDICATION_OUTCOMES.GIVEN
              ? ""
              : getMedicationOutcome(prev.medicationOutcomes, medicationId)
                  .reason,
        },
      },
    }));
  };

  const handleMedicationOutcomeReasonChange = (medicationId, reason) => {
    setMedDraft((prev) => ({
      ...prev,
      medicationOutcomes: {
        ...(prev.medicationOutcomes || {}),
        [medicationId]: {
          ...getMedicationOutcome(prev.medicationOutcomes, medicationId),
          reason,
        },
      },
    }));
  };

  const createDraftMedicationFromMedsModal = async () => {
    if (!canWrite || !activeCareProfile?.id || !currentUser?.uid) return;
    const trimmedName = String(newMedicationName || "").trim();
    if (!trimmedName) {
      window.alert("Please enter a medication name.");
      return;
    }

    setIsCreatingDraftMedication(true);
    const now = Timestamp.now();
    const actor = {
      uid: currentUser.uid,
      name: currentUser.displayName || currentUser.email || "",
    };
    try {
      const docRef = doc(collection(db, "medications"));
      await commitFirestoreWrite(
        setDoc(docRef, {
          medicationName: trimmedName,
          status: "Draft",
          careProfileId: activeCareProfile.id,
          userId: activeCareProfile.userId,
          createdAt: now,
          createdBy: actor,
          lastModifiedAt: now,
          lastModifiedBy: actor,
        }),
        "Create draft medication from daily care log",
      );
      const newId = docRef.id;
      setMedDraft((prev) => ({
        ...prev,
        medicationIds: prev.medicationIds.includes(newId)
          ? prev.medicationIds
          : [...prev.medicationIds, newId],
        medicationDosages: {
          ...(prev.medicationDosages || {}),
          [newId]: prev.medicationDosages?.[newId] || "",
        },
        medicationOutcomes: {
          ...(prev.medicationOutcomes || {}),
          [newId]: getMedicationOutcome(prev.medicationOutcomes, newId),
        },
      }));
      setNewMedicationName("");
      setShowAddMedicationInline(false);
      setToastMessage(offlineAwareMessage("Draft medication added"));
    } catch (error) {
      console.error(
        "Failed to create draft medication from daily care log",
        error,
      );
      window.alert("Unable to add draft medication. Please try again.");
    } finally {
      setIsCreatingDraftMedication(false);
    }
  };

  const saveMedsSegment = async () => {
    if (
      !Array.isArray(medDraft.medicationIds) ||
      medDraft.medicationIds.length === 0
    ) {
      window.alert("Select at least one medication before inserting.");
      return;
    }
    const missingDosages = (medDraft.medicationIds || []).filter((id) => {
      const outcome = getMedicationOutcome(medDraft.medicationOutcomes, id);
      return (
        isMedicationGiven(outcome) &&
        !String(medDraft.medicationDosages?.[id] || "").trim()
      );
    });
    if (missingDosages.length > 0) {
      window.alert("Please enter dosage for each medication marked Given.");
      return;
    }
    const missingReasons = (medDraft.medicationIds || []).filter((id) => {
      const outcome = getMedicationOutcome(medDraft.medicationOutcomes, id);
      return (
        !isMedicationGiven(outcome) && !String(outcome.reason || "").trim()
      );
    });
    if (missingReasons.length > 0) {
      window.alert("Please select a reason for each held/skipped medication.");
      return;
    }
    await upsertSegment({
      type: "meds",
      data: {
        medicationIds: medDraft.medicationIds,
        medicationDosages: medDraft.medicationDosages || {},
        medicationOutcomes: medDraft.medicationOutcomes || {},
        note: medDraft.note || "",
      },
    });
  };

  const saveNoteSegment = async () => {
    if (isBlank(noteDraft)) {
      if (editingSegmentId) {
        await removeSegment(editingSegmentId);
        return;
      }
      window.alert("Enter a note before inserting.");
      return;
    }

    const isEditingBundledNote =
      Boolean(editingEntryId) &&
      Boolean(editingSegmentId) &&
      (draftSegments || []).some((segment) => segment.type !== "note");

    if (isEditingBundledNote) {
      const trimmedNote = noteDraft.trim();
      const remainingSegments = (draftSegments || []).filter(
        (segment) => segment.id !== editingSegmentId,
      );
      await persistEntry(remainingSegments, {
        entryId: editingEntryId,
        skipReset: true,
      });
      await persistEntry([{ type: "note", data: { text: trimmedNote } }], {
        createNew: true,
      });
      setSaveFeedback("Note moved to its own event");
      setToastMessage("Note moved to its own event");
      return;
    }

    await upsertSegment({ type: "note", data: { text: noteDraft.trim() } });
  };

  const saveIncidentSegment = async () => {
    const description = String(incidentDraft.description || "").trim();
    const actionTaken = String(incidentDraft.actionTaken || "").trim();
    if (!description || !actionTaken) {
      window.alert("Enter both incident description and action taken.");
      return;
    }
    await upsertSegment({
      type: "incident",
      data: {
        type: incidentDraft.type || "Other",
        description,
        actionTaken,
      },
    });
  };

  const derivePayloadFromDraft = (segments = draftSegments) => {
    const vitalsMerged = {};
    const vitalsNotes = [];
    const medications = [];
    const structuredNotes = [];
    const incidents = [];

    (segments || []).forEach((segment) => {
      if (segment.type === "vitals") {
        const segmentData = segment.data || {};
        Object.entries(segmentData).forEach(([key, value]) => {
          if (key === "note") return;
          vitalsMerged[key] = value;
        });
        if (!isBlank(segmentData.note)) {
          vitalsNotes.push(segmentData.note.trim());
        }
      }
      if (segment.type === "meds") {
        const selected = Array.isArray(segment.data?.medicationIds)
          ? segment.data.medicationIds
          : [];
        const note = segment.data?.note || "";
        const medicationDosages = segment.data?.medicationDosages || {};
        const medicationOutcomes = segment.data?.medicationOutcomes || {};
        selected.forEach((medicationId) => {
          const medication = medicationMap[medicationId];
          const outcome = getMedicationOutcome(
            medicationOutcomes,
            medicationId,
          );
          const isGiven = isMedicationGiven(outcome);
          const dosage =
            medicationDosages[medicationId] ||
            medication?.dosage ||
            medication?.strength ||
            "";
          medications.push({
            medicationId,
            medicationName: medication?.medicationName || "Medication",
            linkedInventoryId: medication?.linkedInventoryId || null,
            dosageAdministered: isGiven ? dosage : "",
            dosage: isGiven ? dosage : "",
            frequencyExpected: medication?.frequency || "",
            sourceMedicationStatus: medication?.status || "",
            status: outcome.status || MEDICATION_OUTCOMES.GIVEN,
            holdReason: isGiven ? "" : outcome.reason || "",
            note,
          });
        });
      }
      if (segment.type === "note" && !isBlank(segment.data?.text)) {
        structuredNotes.push(segment.data.text.trim());
      }
      if (segment.type === "incident") {
        const description = String(segment.data?.description || "").trim();
        const actionTaken = String(segment.data?.actionTaken || "").trim();
        if (!description || !actionTaken) return;
        incidents.push({
          type: segment.data?.type || "Other",
          description,
          actionTaken,
        });
      }
    });

    const vitalsNoteText = vitalsNotes.join("\n");
    const noteText = structuredNotes.filter(Boolean).join("\n");
    const eventTypeTags = [];
    if (Object.keys(vitalsMerged).length > 0) eventTypeTags.push("vitals");
    if (medications.length > 0) eventTypeTags.push("meds");
    if (incidents.length > 0) eventTypeTags.push("incident");
    if (noteText || vitalsNoteText) {
      eventTypeTags.push("notes");
    }

    return {
      vitalsMerged,
      vitalsNoteText,
      medications,
      incidents,
      noteText,
      eventTypeTags,
    };
  };

  const normalizeVitalsForVitalsLog = (vitalsMerged) => {
    const processedVitals = {};
    Object.entries(vitalsMerged || {}).forEach(([key, value]) => {
      if (key === "bloodPressure") {
        const systolic = parseFloat(value?.systolic);
        const diastolic = parseFloat(value?.diastolic);
        if (!Number.isNaN(systolic) || !Number.isNaN(diastolic)) {
          processedVitals[key] = {
            systolic: Number.isNaN(systolic) ? null : systolic,
            diastolic: Number.isNaN(diastolic) ? null : diastolic,
          };
        }
        return;
      }

      const numericValue = parseFloat(value);
      if (!Number.isNaN(numericValue)) {
        processedVitals[key] = numericValue;
      }
    });
    return processedVitals;
  };

  const syncLinkedVitalsLog = async ({ entryId, payload, now }) => {
    const existingVitalsQuery = query(
      collection(db, "vitalsLog"),
      where("dailyCareLogId", "==", entryId),
    );
    const existingVitalsSnap = await getDocs(existingVitalsQuery);
    const processedVitals = normalizeVitalsForVitalsLog(payload.vitals);
    const hasVitals = Object.keys(processedVitals).length > 0;

    if (!hasVitals) {
      await Promise.all(
        existingVitalsSnap.docs.map((item) => deleteDoc(item.ref)),
      );
      return;
    }

    const vitalsPayload = {
      vitals: processedVitals,
      dateTime: payload.occurredAt,
      notes: payload.vitalsNoteText || "",
      careProfileId: activeCareProfile.id,
      userId: activeCareProfile.userId,
      dailyCareLogId: entryId,
      source: "dailyCareLog",
      lastModifiedAt: now,
      lastModifiedBy: payload.lastModifiedBy,
    };

    const [primaryDoc, ...duplicateDocs] = existingVitalsSnap.docs;
    if (primaryDoc) {
      await updateDoc(primaryDoc.ref, vitalsPayload);
      await Promise.all(duplicateDocs.map((item) => deleteDoc(item.ref)));
      return;
    }

    await addDoc(collection(db, "vitalsLog"), {
      ...vitalsPayload,
      createdAt: now,
      createdBy: payload.recordedBy,
    });
  };

  const getGivenMedicationCounts = (medications = []) =>
    medications.reduce((counts, medication) => {
      if (
        !medication?.medicationId ||
        (medication.status || MEDICATION_OUTCOMES.GIVEN) !==
          MEDICATION_OUTCOMES.GIVEN
      ) {
        return counts;
      }
      counts[medication.medicationId] =
        (counts[medication.medicationId] || 0) + 1;
      return counts;
    }, {});

  const deductInventoryForMedication = async ({
    medication,
    logId,
    administeredAt,
    actor,
    now,
    count,
  }) => {
    if (!activeCareProfile?.id || !medication?.medicationId || count <= 0) {
      return;
    }

    try {
      const inventoryConstraints = medication.linkedInventoryId
        ? [
            where("careProfileId", "==", activeCareProfile.id),
            where("__name__", "==", medication.linkedInventoryId),
          ]
        : [
            where("careProfileId", "==", activeCareProfile.id),
            where("medicationId", "==", medication.medicationId),
          ];
      const inventoryQuery = query(
        collection(db, "medicationInventory"),
        ...inventoryConstraints,
      );
      const inventorySnapshot = await getDocs(inventoryQuery);
      if (inventorySnapshot.empty) return;

      const inventoryDoc = inventorySnapshot.docs[0];
      const inventoryItem = {
        id: inventoryDoc.id,
        ...inventoryDoc.data(),
      };
      const quantityBefore = toInventoryNumber(inventoryItem.quantityOnHand);
      const deduction =
        toInventoryNumber(inventoryItem.deductQuantityPerAdministration, 1) *
        count;
      if (deduction <= 0) return;

      const quantityAfter = Math.max(0, quantityBefore - deduction);
      await updateDoc(doc(db, "medicationInventory", inventoryItem.id), {
        quantityOnHand: quantityAfter,
        lastModifiedAt: now,
        lastModifiedBy: actor,
      });

      await addDoc(collection(db, "medicationInventoryAdjustments"), {
        careProfileId: activeCareProfile.id,
        medicationInventoryId: inventoryItem.id,
        medicationId: medication.medicationId,
        medicationName: medication.medicationName,
        source: "dailyCareLog",
        sourceId: logId,
        dailyCareLogId: medication.dailyCareLogId || null,
        delta: quantityAfter - quantityBefore,
        quantityBefore,
        quantityAfter,
        reason: `Given administration logged from Daily Care Log at ${administeredAt
          .toDate()
          .toLocaleString()}`,
        createdAt: now,
        createdBy: actor,
      });
    } catch (error) {
      console.error("Error deducting daily care medication inventory:", error);
    }
  };

  const deductInventoryForNewGivenMedications = async ({
    medications,
    existingGivenCounts,
    administeredAt,
    actor,
    now,
  }) => {
    const nextGivenCounts = getGivenMedicationCounts(
      medications.map(({ medication }) => medication),
    );

    await Promise.all(
      Object.entries(nextGivenCounts).map(async ([medicationId, nextCount]) => {
        const deductionCount =
          nextCount - (existingGivenCounts?.[medicationId] || 0);
        if (deductionCount <= 0) return;

        const createdLog = medications.find(
          ({ medication }) =>
            medication.medicationId === medicationId &&
            (medication.status || MEDICATION_OUTCOMES.GIVEN) ===
              MEDICATION_OUTCOMES.GIVEN,
        );
        if (!createdLog) return;

        await deductInventoryForMedication({
          medication: createdLog.medication,
          logId: createdLog.logId,
          administeredAt,
          actor,
          now,
          count: deductionCount,
        });
      }),
    );
  };

  const getLinkedMedicationLogs = async (entryId) => {
    const medsQuery = query(
      collection(db, "medicationAdministrationLog"),
      where("dailyCareLogId", "==", entryId),
    );
    const medsSnap = await getDocs(medsQuery);
    return medsSnap.docs.map((item) => ({
      id: item.id,
      ...item.data(),
    }));
  };

  const restockInventoryForMedication = async ({
    medication,
    entryId,
    actor,
    now,
    count,
  }) => {
    if (!activeCareProfile?.id || !medication?.medicationId || count <= 0) {
      return;
    }

    try {
      const inventoryQuery = query(
        collection(db, "medicationInventory"),
        where("careProfileId", "==", activeCareProfile.id),
        where("medicationId", "==", medication.medicationId),
      );
      const inventorySnapshot = await getDocs(inventoryQuery);
      if (inventorySnapshot.empty) return;

      const inventoryDoc = inventorySnapshot.docs[0];
      const inventoryItem = {
        id: inventoryDoc.id,
        ...inventoryDoc.data(),
      };
      const quantityBefore = toInventoryNumber(inventoryItem.quantityOnHand);
      const restockQuantity =
        toInventoryNumber(inventoryItem.deductQuantityPerAdministration, 1) *
        count;
      if (restockQuantity <= 0) return;

      const quantityAfter = quantityBefore + restockQuantity;
      await updateDoc(doc(db, "medicationInventory", inventoryItem.id), {
        quantityOnHand: quantityAfter,
        lastModifiedAt: now,
        lastModifiedBy: actor,
      });

      await addDoc(collection(db, "medicationInventoryAdjustments"), {
        careProfileId: activeCareProfile.id,
        medicationInventoryId: inventoryItem.id,
        medicationId: medication.medicationId,
        medicationName: medication.medicationName,
        source: "dailyCareLogDelete",
        sourceId: entryId,
        dailyCareLogId: entryId,
        delta: quantityAfter - quantityBefore,
        quantityBefore,
        quantityAfter,
        reason: "Given administration removed from Daily Care Log",
        createdAt: now,
        createdBy: actor,
      });
    } catch (error) {
      console.error("Error restocking daily care medication inventory:", error);
    }
  };

  const restockInventoryForLinkedGivenMedications = async ({
    entryId,
    actor,
    now,
  }) => {
    const medicationLogs = await getLinkedMedicationLogs(entryId);
    const givenCounts = getGivenMedicationCounts(medicationLogs);

    await Promise.all(
      Object.entries(givenCounts).map(async ([medicationId, count]) => {
        const medication = medicationLogs.find(
          (item) =>
            item.medicationId === medicationId &&
            (item.status || MEDICATION_OUTCOMES.GIVEN) ===
              MEDICATION_OUTCOMES.GIVEN,
        );
        if (!medication) return;

        await restockInventoryForMedication({
          medication,
          entryId,
          actor,
          now,
          count,
        });
      }),
    );
  };

  const deductInventoryForLinkedGivenMedications = async ({
    entryId,
    actor,
    now,
  }) => {
    const medicationLogs = await getLinkedMedicationLogs(entryId);

    await Promise.all(
      Object.entries(getGivenMedicationCounts(medicationLogs)).map(
        async ([medicationId, count]) => {
          const medication = medicationLogs.find(
            (item) =>
              item.medicationId === medicationId &&
              (item.status || MEDICATION_OUTCOMES.GIVEN) ===
                MEDICATION_OUTCOMES.GIVEN,
          );
          if (!medication) return;

          await deductInventoryForMedication({
            medication,
            logId: medication.id,
            administeredAt: medication.administeredAt || now,
            actor,
            now,
            count,
          });
        },
      ),
    );
  };

  const syncLinkedMedicationLogs = async ({ entryId, payload, now }) => {
    const existingMedsQuery = query(
      collection(db, "medicationAdministrationLog"),
      where("dailyCareLogId", "==", entryId),
    );
    const existingMedsSnap = await getDocs(existingMedsQuery);
    const existingGivenCounts = getGivenMedicationCounts(
      existingMedsSnap.docs.map((item) => item.data()),
    );
    await Promise.all(existingMedsSnap.docs.map((item) => deleteDoc(item.ref)));

    if (
      !Array.isArray(payload.medications) ||
      payload.medications.length === 0
    ) {
      return;
    }

    const createdMedicationLogs = await Promise.all(
      payload.medications.map(async (medication) => {
        const logRef = await addDoc(
          collection(db, "medicationAdministrationLog"),
          {
            medicationId: medication.medicationId,
            medicationName: medication.medicationName,
            linkedInventoryId: medication.linkedInventoryId || null,
            dosageAdministered: medication.dosageAdministered,
            frequencyExpected: medication.frequencyExpected || "",
            administeredAt: payload.occurredAt,
            status: medication.status || MEDICATION_OUTCOMES.GIVEN,
            holdReason: medication.holdReason || "",
            notes: medication.note || "",
            careProfileId: activeCareProfile.id,
            userId: activeCareProfile.userId,
            dailyCareLogId: entryId,
            source: "dailyCareLog",
            createdAt: now,
            createdBy: payload.recordedBy,
            lastModifiedAt: now,
            lastModifiedBy: payload.lastModifiedBy,
          },
        );
        return {
          medication: { ...medication, dailyCareLogId: entryId },
          logId: logRef.id,
        };
      }),
    );
    await deductInventoryForNewGivenMedications({
      medications: createdMedicationLogs,
      existingGivenCounts,
      administeredAt: payload.occurredAt,
      actor: payload.recordedBy,
      now,
    });
  };

  const syncLinkedIncidentReports = async ({ entryId, payload, now }) => {
    const existingIncidentsQuery = query(
      collection(db, "incidentReports"),
      where("dailyCareLogId", "==", entryId),
    );
    const existingIncidentsSnap = await getDocs(existingIncidentsQuery);
    await Promise.all(
      existingIncidentsSnap.docs.map((item) => deleteDoc(item.ref)),
    );

    if (!Array.isArray(payload.incidents) || payload.incidents.length === 0) {
      return;
    }

    await Promise.all(
      payload.incidents.map((incident) =>
        addDoc(collection(db, "incidentReports"), {
          dateTime: payload.occurredAt,
          type: incident.type || "Other",
          description: incident.description,
          actionTaken: incident.actionTaken,
          careProfileId: activeCareProfile.id,
          userId: activeCareProfile.userId,
          dailyCareLogId: entryId,
          source: "dailyCareLog",
          createdAt: now,
          createdBy: payload.recordedBy,
          lastModifiedAt: now,
          lastModifiedBy: payload.lastModifiedBy,
        }),
      ),
    );
  };

  const syncLinkedNotesObservations = async ({ entryId, payload, now }) => {
    const existingNotesQuery = query(
      collection(db, "notesObservations"),
      where("dailyCareLogId", "==", entryId),
    );
    const existingNotesSnap = await getDocs(existingNotesQuery);
    const noteText = String(payload.noteText || "").trim();

    if (!noteText) {
      await Promise.all(
        existingNotesSnap.docs.map((item) => deleteDoc(item.ref)),
      );
      return;
    }

    const notesPayload = {
      noteText,
      careProfileId: activeCareProfile.id,
      userId: activeCareProfile.userId,
      dailyCareLogId: entryId,
      source: "dailyCareLog",
      lastModifiedAt: now,
      lastModifiedBy: payload.lastModifiedBy,
    };

    const [primaryDoc, ...duplicateDocs] = existingNotesSnap.docs;
    if (primaryDoc) {
      await updateDoc(primaryDoc.ref, notesPayload);
      await Promise.all(duplicateDocs.map((item) => deleteDoc(item.ref)));
      return;
    }

    await addDoc(collection(db, "notesObservations"), {
      ...notesPayload,
      createdAt: payload.occurredAt,
      createdBy: payload.recordedBy,
    });
  };

  const syncLinkedCareRecords = async ({ entryId, payload, now }) => {
    await Promise.all([
      syncLinkedVitalsLog({ entryId, payload, now }),
      syncLinkedMedicationLogs({ entryId, payload, now }),
      syncLinkedIncidentReports({ entryId, payload, now }),
      syncLinkedNotesObservations({ entryId, payload, now }),
    ]);
  };

  const deleteLinkedCareRecords = async (entryId) => {
    const linkedCollections = [
      "vitalsLog",
      "medicationAdministrationLog",
      "incidentReports",
      "notesObservations",
    ];

    await Promise.all(
      linkedCollections.map(async (collectionName) => {
        const linkedQuery = query(
          collection(db, collectionName),
          where("dailyCareLogId", "==", entryId),
        );
        const linkedSnap = await getDocs(linkedQuery);
        await Promise.all(linkedSnap.docs.map((item) => deleteDoc(item.ref)));
      }),
    );
  };

  const persistEntry = async (segmentsToSave, options = {}) => {
    const {
      entryId = editingEntryId,
      createNew = false,
      skipReset = false,
    } = options;
    if (!activeCareProfile?.id || !currentUser || !canWrite) return;
    if (!entryTime) {
      window.alert("Please enter a valid time.");
      return;
    }
    const {
      vitalsMerged,
      vitalsNoteText,
      medications,
      incidents,
      noteText,
      eventTypeTags,
    } = derivePayloadFromDraft(segmentsToSave);
    if (
      Object.keys(vitalsMerged).length === 0 &&
      medications.length === 0 &&
      incidents.length === 0 &&
      !noteText &&
      !vitalsNoteText
    ) {
      window.alert("Insert at least one structured block before saving.");
      return;
    }

    const occurredAt = timestampFromDateAndTime(selectedDate, entryTime);
    const now = Timestamp.now();
    const payload = {
      careProfileId: activeCareProfile.id,
      userId: currentUser.uid,
      date: selectedDate,
      time: entryTime,
      occurredAt,
      timestamp: occurredAt,
      eventTypeTags,
      vitals: vitalsMerged,
      medications,
      incidents,
      vitalsNoteText,
      noteText,
      segments: segmentsToSave,
      recordedBy: {
        uid: currentUser.uid,
        name: currentUser.displayName || "",
        email: currentUser.email || "",
      },
      updatedAt: now,
      lastModifiedAt: now,
      lastModifiedBy: {
        uid: currentUser.uid,
        name: currentUser.displayName || "",
        email: currentUser.email || "",
      },
    };

    try {
      setIsSaving(true);
      let persistedEntryId = entryId;
      let queuedWrite = false;
      if (entryId && !createNew) {
        const result = await commitFirestoreWrite(
          updateDoc(doc(db, "dailyCareLogs", entryId), payload),
          "Update daily care log entry",
        );
        queuedWrite = result.queued;
        setSaveFeedback(offlineAwareMessage("Care event updated"));
      } else {
        const entryRef = doc(collection(db, "dailyCareLogs"));
        const result = await commitFirestoreWrite(
          setDoc(entryRef, {
            ...payload,
            createdAt: now,
            createdBy: {
              uid: currentUser.uid,
              name: currentUser.displayName || "",
              email: currentUser.email || "",
            },
          }),
          "Create daily care log entry",
        );
        queuedWrite = result.queued;
        persistedEntryId = entryRef.id;
        setSaveFeedback(offlineAwareMessage("Care event saved"));
      }
      try {
        const linkedSyncPromise = syncLinkedCareRecords({
          entryId: persistedEntryId,
          payload,
          now,
        });
        if (queuedWrite || isOffline()) {
          linkedSyncPromise.catch((syncError) => {
            console.error("Failed to sync linked care records", syncError);
          });
        } else {
          await linkedSyncPromise;
        }
      } catch (syncError) {
        console.error("Failed to sync linked care records", syncError);
        window.alert(
          "Care event saved, but linked Vitals, Medication, Notes, or Incident records could not be updated. Please check permissions and try again if needed.",
        );
      }
      if (!skipReset) {
        resetDraft();
      }
      setToastMessage(
        offlineAwareMessage(
          entryId && !createNew ? "Care event updated" : "Care event saved",
        ),
      );
    } catch (error) {
      console.error("Failed to save daily care log entry", error);
      const errorCode = error?.code || "";
      if (errorCode === "permission-denied") {
        window.alert(
          "Save blocked by Firestore permissions. Please deploy the latest firestore.rules for dailyCareLogs or confirm this user has access to the active profile.",
        );
      } else if (errorCode === "failed-precondition") {
        window.alert(
          "Save blocked by missing Firestore index. Please deploy firestore.indexes.json and retry.",
        );
      } else {
        window.alert(
          `Unable to save this care event. ${errorCode ? `(${errorCode})` : ""} Please try again.`,
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const deleteEntry = async (entryId) => {
    if (!entryId || !canWrite) return;
    const confirmed = window.confirm("Move this care event to trash?");
    if (!confirmed) return;

    try {
      setIsSaving(true);
      const now = Timestamp.now();
      const actor = {
        uid: currentUser?.uid || "",
        name: currentUser?.displayName || "",
        email: currentUser?.email || "",
      };
      const entrySnap = await getDoc(doc(db, "dailyCareLogs", entryId));
      const existingRestoredAt =
        entrySnap.data()?.medicationInventoryRestoredAt;
      const existingRestoredBy =
        entrySnap.data()?.medicationInventoryRestoredBy;
      const shouldRestockInventory = !existingRestoredAt;
      if (shouldRestockInventory) {
        await restockInventoryForLinkedGivenMedications({
          entryId,
          actor,
          now,
        });
      }
      await updateDoc(doc(db, "dailyCareLogs", entryId), {
        isTrashed: true,
        trashedAt: now,
        trashedBy: actor,
        medicationInventoryRestoredAt: existingRestoredAt || now,
        medicationInventoryRestoredBy: existingRestoredBy || actor,
        lastModifiedAt: now,
        lastModifiedBy: actor,
      });
      if (editingEntryId === entryId) {
        resetDraft();
      } else {
        setSaveFeedback("Care event moved to trash");
      }
      setToastMessage("Care event moved to trash");
      setOpenMenuEntryId(null);
    } catch (error) {
      console.error("Failed to delete daily care log entry", error);
      const errorCode = error?.code || "";
      if (errorCode === "permission-denied") {
        window.alert(
          "Trash action blocked by Firestore permissions. Please deploy the latest firestore.rules for dailyCareLogs or confirm this user has access to the active profile.",
        );
      } else if (errorCode === "failed-precondition") {
        window.alert(
          "Trash action blocked by missing Firestore index. Please deploy firestore.indexes.json and retry.",
        );
      } else {
        window.alert(
          `Unable to move this care event to trash. ${errorCode ? `(${errorCode})` : ""} Please try again.`,
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const restoreEntry = async (entryId) => {
    if (!entryId || !canWrite) return;
    try {
      setIsSaving(true);
      const now = Timestamp.now();
      const entrySnap = await getDoc(doc(db, "dailyCareLogs", entryId));
      const shouldRedeductInventory = Boolean(
        entrySnap.data()?.medicationInventoryRestoredAt,
      );
      const actor = {
        uid: currentUser?.uid || "",
        name: currentUser?.displayName || "",
        email: currentUser?.email || "",
      };
      if (shouldRedeductInventory) {
        await deductInventoryForLinkedGivenMedications({
          entryId,
          actor,
          now,
        });
      }
      await updateDoc(doc(db, "dailyCareLogs", entryId), {
        isTrashed: false,
        trashedAt: null,
        trashedBy: null,
        medicationInventoryRestoredAt: null,
        medicationInventoryRestoredBy: null,
        lastModifiedAt: now,
        lastModifiedBy: actor,
      });
      setSaveFeedback("Care event restored");
      setToastMessage("Care event restored");
      setOpenMenuEntryId(null);
    } catch (error) {
      console.error("Failed to restore daily care log entry", error);
      const errorCode = error?.code || "";
      window.alert(
        `Unable to restore this care event. ${errorCode ? `(${errorCode})` : ""} Please try again.`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const permanentlyDeleteEntry = async (entryId) => {
    if (!entryId || !canWrite) return;
    const confirmed = window.confirm(
      "Permanently delete this care event? This cannot be undone.",
    );
    if (!confirmed) return;
    try {
      setIsSaving(true);
      const now = Timestamp.now();
      const actor = {
        uid: currentUser?.uid || "",
        name: currentUser?.displayName || "",
        email: currentUser?.email || "",
      };
      const entrySnap = await getDoc(doc(db, "dailyCareLogs", entryId));
      const shouldRestockInventory =
        !entrySnap.data()?.medicationInventoryRestoredAt;
      if (shouldRestockInventory) {
        await restockInventoryForLinkedGivenMedications({
          entryId,
          actor,
          now,
        });
      }
      await deleteLinkedCareRecords(entryId);
      await deleteDoc(doc(db, "dailyCareLogs", entryId));
      if (editingEntryId === entryId) {
        resetDraft();
      }
      setSaveFeedback("Care event permanently deleted");
      setToastMessage("Care event permanently deleted");
      setOpenMenuEntryId(null);
    } catch (error) {
      console.error("Failed to permanently delete daily care log entry", error);
      const errorCode = error?.code || "";
      window.alert(
        `Unable to permanently delete this care event. ${errorCode ? `(${errorCode})` : ""} Please try again.`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const hydrateForEdit = (entry) => {
    setEditingEntryId(entry.id);
    setEntryTime(entry.time || getCurrentTime());
    const existingSegments =
      Array.isArray(entry.segments) && entry.segments.length > 0
        ? entry.segments.map((segment) => ({
            ...segment,
            id: segment.id || makeSegmentId(),
          }))
        : [];

    const hasVitalsSegment = existingSegments.some(
      (segment) => segment.type === "vitals",
    );
    const hasMedsSegment = existingSegments.some(
      (segment) => segment.type === "meds",
    );
    const hasNoteSegment = existingSegments.some(
      (segment) => segment.type === "note",
    );
    const hasIncidentSegment = existingSegments.some(
      (segment) => segment.type === "incident",
    );

    if (
      !hasVitalsSegment &&
      entry.vitals &&
      Object.keys(entry.vitals).length > 0
    ) {
      existingSegments.push({
        id: makeSegmentId(),
        type: "vitals",
        data: {
          ...entry.vitals,
          note: entry.vitalsNoteText || "",
        },
      });
    }

    if (
      !hasMedsSegment &&
      Array.isArray(entry.medications) &&
      entry.medications.length > 0
    ) {
      const medicationDosages = entry.medications.reduce((acc, medication) => {
        if (medication?.medicationId) {
          acc[medication.medicationId] =
            medication.dosageAdministered || medication.dosage || "";
        }
        return acc;
      }, {});
      const medicationOutcomes = entry.medications.reduce((acc, medication) => {
        if (medication?.medicationId) {
          acc[medication.medicationId] = {
            status: medication.status || MEDICATION_OUTCOMES.GIVEN,
            reason: medication.holdReason || "",
          };
        }
        return acc;
      }, {});
      existingSegments.push({
        id: makeSegmentId(),
        type: "meds",
        data: {
          medicationIds: entry.medications.map(
            (medication) => medication.medicationId,
          ),
          medicationDosages,
          medicationOutcomes,
          note: entry.medications[0]?.note || "",
        },
      });
    }

    if (!hasNoteSegment && !isBlank(entry.noteText)) {
      existingSegments.push({
        id: makeSegmentId(),
        type: "note",
        data: { text: entry.noteText },
      });
    }
    if (
      !hasIncidentSegment &&
      Array.isArray(entry.incidents) &&
      entry.incidents.length > 0
    ) {
      entry.incidents.forEach((incident) => {
        existingSegments.push({
          id: makeSegmentId(),
          type: "incident",
          data: {
            type: incident?.type || "Other",
            description: incident?.description || "",
            actionTaken: incident?.actionTaken || "",
          },
        });
      });
    }
    setDraftSegments(existingSegments);
    if (existingSegments.length > 0) {
      const preferredSegment =
        existingSegments.find((segment) => segment.type === "note") ||
        existingSegments[0];
      openModal(preferredSegment.type, preferredSegment);
      setSaveFeedback("Editing entry");
      return;
    }
    setSaveFeedback("No editable segments found for this event.");
  };

  const renderVitalsSummary = (entryVitals) => {
    if (!entryVitals || Object.keys(entryVitals).length === 0) return null;
    return (
      <div className="space-y-1">
        <p className="text-sm font-semibold text-gray-800">Vitals Recorded</p>
        <div className="text-sm text-gray-700 flex flex-wrap gap-x-4 gap-y-1">
          {enabledVitals.map((field) => {
            const value = entryVitals[field.id];
            if (!value) return null;
            if (field.id === "bloodPressure") {
              return (
                <span key={field.id}>
                  {field.label}: {value.systolic || "-"}/
                  {value.diastolic || "-"}
                </span>
              );
            }
            return (
              <span key={field.id}>
                {field.label}: {value}
                {field.unit ? ` ${field.unit}` : ""}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSegmentPreview = (segment) => {
    if (segment.type === "vitals") {
      const previewText = Object.entries(segment.data || {})
        .filter(([key]) => key !== "note")
        .map(([key, value]) => {
          const field = enabledVitals.find((item) => item.id === key);
          const label = field?.label || key;
          if (key === "bloodPressure") {
            return `${label}: ${value.systolic || "-"}/${value.diastolic || "-"}`;
          }
          return `${label}: ${value}`;
        })
        .join(", ");
      return (
        <div className="text-sm text-gray-700">
          <span className="font-medium">Vitals</span> {previewText}
          {segment.data?.note ? ` | Note: ${segment.data.note}` : ""}
        </div>
      );
    }
    if (segment.type === "meds") {
      const names = (segment.data?.medicationIds || [])
        .map((id) => {
          const medication = medicationMap[id];
          const medName = medication?.medicationName || id;
          const dosage = segment.data?.medicationDosages?.[id] || "";
          const outcome = getMedicationOutcome(
            segment.data?.medicationOutcomes,
            id,
          );
          const label =
            String(medication?.status || "").toLowerCase() === "draft"
              ? `${medName} (Draft)`
              : medName;
          if (!isMedicationGiven(outcome)) {
            return `${label} ${outcome.status}${outcome.reason ? ` (${outcome.reason})` : ""}`;
          }
          return dosage ? `${label} ${dosage}` : label;
        })
        .join(", ");
      return (
        <div className="text-sm text-gray-700">
          <span className="font-medium">Meds</span>{" "}
          {names || "No medications selected"}
          {segment.data?.note ? ` | Note: ${segment.data.note}` : ""}
        </div>
      );
    }
    if (segment.type === "incident") {
      return (
        <div className="text-sm text-gray-700">
          <span className="font-medium">Incident</span>{" "}
          {(segment.data?.type || "Other") + ": "}
          {segment.data?.description || ""}
        </div>
      );
    }
    return (
      <div className="text-sm text-gray-700">
        <span className="font-medium">Note</span> {segment.data?.text || ""}
      </div>
    );
  };

  if (!activeCareProfile) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800">
          No active care profile selected
        </h2>
        <p className="mt-2 text-gray-600">
          Select a profile to start documenting daily care events.
        </p>
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800">Access denied</h2>
        <p className="mt-2 text-gray-600">
          You do not have permission to view this Daily Care Log.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-10">
      <section className="rounded-3xl border border-slate-200 bg-white/95 p-5 shadow-sm md:p-7">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">Daily Care Log</p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
              {toDisplayDate(selectedDate)}
            </h1>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Link
              to="/dashboard"
              className="inline-flex h-11 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 px-4 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Dashboard At a Glance
            </Link>
            <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-1.5">
              <button
                type="button"
                onClick={() =>
                  setSelectedDate((prev) => withDateShift(prev, -1))
                }
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100"
              >
                <FaChevronLeft />
              </button>
              <input
                type="date"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <button
                type="button"
                onClick={() => setSelectedDate(getTodayDate())}
                className="h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() =>
                  setSelectedDate((prev) => withDateShift(prev, 1))
                }
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-5 rounded-3xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-5 shadow-sm md:p-7">
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <span className="text-xs font-medium text-slate-500">
            Insert and save care blocks
          </span>
        </div>

        <div className="flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={() => openModal("vitals")}
            disabled={!canWrite}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-100 disabled:opacity-50"
          >
            <FaHeartbeat />
            Insert Vitals
          </button>
          <button
            type="button"
            onClick={() => openModal("meds")}
            disabled={!canWrite}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
          >
            <FaPills />
            Insert Meds
          </button>
          <button
            type="button"
            onClick={() => openModal("note")}
            disabled={!canWrite}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 transition hover:bg-violet-100 disabled:opacity-50"
          >
            <FaStickyNote />
            Insert Note
          </button>
          <button
            type="button"
            onClick={() => openModal("incident")}
            disabled={!canWrite}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-medium text-orange-700 transition hover:bg-orange-100 disabled:opacity-50"
          >
            <FaExclamationTriangle />
            Insert Incident
          </button>
        </div>

        {undoState && (
          <div className="flex items-center justify-between rounded-2xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm">
            <span className="font-medium text-indigo-800">
              Inserted {undoState.label} block.
            </span>
            <button
              type="button"
              className="rounded-xl border border-indigo-300 px-3 py-1 font-medium text-indigo-700 transition hover:bg-indigo-100"
              onClick={undoLastInsert}
            >
              Undo
            </button>
          </div>
        )}

        {draftSegments.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mt-3 space-y-2">
              {draftSegments.map((segment) => (
                <div
                  key={segment.id}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5"
                >
                  <div className="flex items-start justify-between gap-2">
                    {renderSegmentPreview(segment)}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-white"
                        onClick={() => openModal(segment.type, segment)}
                        disabled={!canWrite}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 transition hover:bg-red-50"
                        onClick={() => removeSegment(segment.id)}
                        disabled={!canWrite}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {saveFeedback && (
          <p className="text-sm font-medium text-slate-600">{saveFeedback}</p>
        )}
      </section>

      <section className="space-y-3 transition-all duration-200">
        <div className="inline-flex items-center rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => {
              setViewMode("active");
              setOpenMenuEntryId(null);
            }}
            className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
              viewMode === "active"
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            Care Events ({entries.length})
          </button>
          <button
            type="button"
            onClick={() => {
              setViewMode("trash");
              setOpenMenuEntryId(null);
            }}
            className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
              viewMode === "trash"
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            Trash ({trashedEntries.length})
          </button>
        </div>

        {viewMode === "active" && entries.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-600 shadow-sm">
            No care events logged for this day yet.
          </div>
        )}
        {viewMode === "trash" && trashedEntries.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-600 shadow-sm">
            Trash is empty for this day.
          </div>
        )}

        {(viewMode === "trash" ? trashedEntries : entries).map((entry) => {
          const meds = Array.isArray(entry.medications)
            ? entry.medications
            : [];
          const hasVitals =
            entry.vitals && Object.keys(entry.vitals).length > 0;
          const hasMeds = meds.length > 0;
          const incidents = Array.isArray(entry.incidents)
            ? entry.incidents
            : [];
          const hasIncident = incidents.length > 0;
          const hasVitalsNote = !isBlank(entry.vitalsNoteText);
          const hasNote = !isBlank(entry.noteText);

          return (
            <article
              key={entry.id}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-transform duration-200 hover:-translate-y-0.5 hover:shadow md:p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-lg font-semibold text-slate-900">
                  {toDisplayTime(entry.time)}
                </h3>
                {canWrite && (
                  <div
                    ref={openMenuEntryId === entry.id ? menuContainerRef : null}
                    className="relative"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setOpenMenuEntryId((prev) =>
                          prev === entry.id ? null : entry.id,
                        )
                      }
                      disabled={isSaving}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                      aria-label="More actions"
                    >
                      <FaEllipsisH />
                    </button>
                    {openMenuEntryId === entry.id && (
                      <div className="absolute right-0 z-20 mt-2 w-52 overflow-hidden rounded-2xl border border-slate-200 bg-white p-1.5 shadow-lg">
                        {viewMode === "active" ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setOpenMenuEntryId(null);
                                hydrateForEdit(entry);
                              }}
                              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                            >
                              <FaEdit />
                              Edit event
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteEntry(entry.id)}
                              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50"
                            >
                              <FaTrash />
                              Move to trash
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => restoreEntry(entry.id)}
                              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                            >
                              Restore event
                            </button>
                            <button
                              type="button"
                              onClick={() => permanentlyDeleteEntry(entry.id)}
                              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50"
                            >
                              <FaTrash />
                              Delete permanently
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-3 space-y-3">
                {hasVitals && renderVitalsSummary(entry.vitals)}
                {hasVitalsNote && (
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-gray-800">
                      Vitals Note
                    </p>
                    <p className="text-sm text-gray-700 break-words whitespace-pre-wrap">
                      {entry.vitalsNoteText}
                    </p>
                  </div>
                )}

                {hasMeds && (
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-gray-800">
                      Medication Administered
                    </p>
                    <p className="text-sm text-gray-700 break-words">
                      {meds
                        .map((med) => {
                          const status =
                            med.status || MEDICATION_OUTCOMES.GIVEN;
                          if (status !== MEDICATION_OUTCOMES.GIVEN) {
                            return `${med.medicationName || "Medication"} ${status}${
                              med.holdReason ? ` (${med.holdReason})` : ""
                            }`;
                          }
                          const dosage = med.dosage ? ` ${med.dosage}` : "";
                          return `${med.medicationName || "Medication"}${dosage}`;
                        })
                        .join(", ")}
                    </p>
                    {meds[0]?.note && (
                      <p className="text-sm text-gray-600 break-words whitespace-pre-wrap">
                        Note: {meds[0].note}
                      </p>
                    )}
                  </div>
                )}
                {hasIncident && (
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-gray-800">
                      Incident Reported
                    </p>
                    {incidents.map((incident, index) => (
                      <div
                        key={`${entry.id}-incident-${index}`}
                        className="text-sm text-gray-700 break-words whitespace-pre-wrap"
                      >
                        <p className="font-medium">
                          {incident.type || "Other"}
                        </p>
                        <p>{incident.description}</p>
                        <p className="text-gray-600">
                          Action Taken: {incident.actionTaken}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {hasNote && (
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-gray-800">Notes</p>
                    <p className="text-sm text-gray-700 break-words whitespace-pre-wrap">
                      {entry.noteText}
                    </p>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </section>

      {activeModal && (
        <div
          className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/30 p-2 backdrop-blur-sm sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="my-2 max-h-[calc(100dvh-1rem)] w-full max-w-xl space-y-4 overflow-y-auto rounded-3xl border border-slate-200 bg-white p-4 shadow-xl sm:my-4 sm:max-h-[calc(100dvh-2rem)] sm:p-5 md:p-6">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-semibold text-slate-900">
                {activeModal === "vitals" && "Insert Vitals Block"}
                {activeModal === "meds" && "Insert Meds Block"}
                {activeModal === "note" && "Insert Note Block"}
                {activeModal === "incident" && "Insert Incident Block"}
              </h3>
              <button
                type="button"
                onPointerDown={blurActiveField}
                onClick={closeModal}
                className="rounded-xl border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-50"
              >
                <FaTimes />
              </button>
            </div>
            <div
              className={
                activeModal === "meds"
                  ? "grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2"
                  : ""
              }
            >
              <div>
                <label
                  htmlFor="insert-block-time"
                  className="mb-1 block text-xs font-medium text-gray-600"
                >
                  Time
                </label>
                <input
                  id="insert-block-time"
                  type="time"
                  value={entryTime}
                  onChange={(event) => setEntryTime(event.target.value)}
                  className="h-11 w-full rounded-xl border border-slate-200 px-3 text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:text-sm"
                />
              </div>
              {activeModal === "meds" && (
                <button
                  type="button"
                  onClick={() => setShowAddMedicationInline((prev) => !prev)}
                  aria-label={
                    showAddMedicationInline
                      ? "Cancel adding medication"
                      : "Add new medication as Draft"
                  }
                  className="h-11 shrink-0 rounded-xl border border-dashed border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  {showAddMedicationInline ? (
                    "Cancel"
                  ) : (
                    <>
                      <span className="sm:hidden">+ Add Draft</span>
                      <span className="hidden sm:inline">
                        + Add new medication as Draft
                      </span>
                    </>
                  )}
                </button>
              )}
            </div>

            {activeModal === "vitals" && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-x-2 gap-y-3 sm:gap-3">
                  {enabledVitals.length === 0 && (
                    <p className="col-span-2 text-sm text-gray-500">
                      No vitals fields are currently enabled in Manage Vitals
                      Fields.
                    </p>
                  )}
                  {enabledVitals.map((field) => {
                    if (field.type === "bp") {
                      return (
                        <div key={field.id} className="col-span-2">
                          <label className="text-xs text-gray-600 block mb-1">
                            {field.label}
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="number"
                              placeholder="Systolic"
                              className="h-10 w-full min-w-0 rounded-lg border border-slate-200 px-2 text-base sm:px-3 sm:text-sm"
                              value={vitalsDraft.bloodPressure?.systolic || ""}
                              onChange={(event) =>
                                setVitalsDraft((prev) => ({
                                  ...prev,
                                  bloodPressure: {
                                    ...(prev.bloodPressure || {}),
                                    systolic: event.target.value,
                                  },
                                }))
                              }
                            />
                            <input
                              type="number"
                              placeholder="Diastolic"
                              className="h-10 w-full min-w-0 rounded-lg border border-slate-200 px-2 text-base sm:px-3 sm:text-sm"
                              value={vitalsDraft.bloodPressure?.diastolic || ""}
                              onChange={(event) =>
                                setVitalsDraft((prev) => ({
                                  ...prev,
                                  bloodPressure: {
                                    ...(prev.bloodPressure || {}),
                                    diastolic: event.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={field.id} className="min-w-0">
                        <label className="text-xs text-gray-600 block mb-1">
                          {field.label} {field.unit ? `(${field.unit})` : ""}
                        </label>
                        <input
                          type="number"
                          className="h-10 w-full min-w-0 rounded-lg border border-slate-200 px-2 text-base sm:px-3 sm:text-sm"
                          value={vitalsDraft[field.id] || ""}
                          onChange={(event) =>
                            setVitalsDraft((prev) => ({
                              ...prev,
                              [field.id]: event.target.value,
                            }))
                          }
                        />
                      </div>
                    );
                  })}
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">
                    Event note (optional)
                  </label>
                  <textarea
                    rows={3}
                    className="w-full min-h-24 resize-y rounded-xl border border-slate-200 px-3 py-2 text-base sm:text-sm"
                    value={vitalsNoteDraft}
                    onChange={(event) => setVitalsNoteDraft(event.target.value)}
                    placeholder="e.g., Taken while patient was resting"
                  />
                </div>
              </div>
            )}

            {activeModal === "meds" && (
              <div className="space-y-3">
                {showAddMedicationInline && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <label className="mb-1 block text-xs font-medium text-slate-600">
                      Medication name
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2"
                        value={newMedicationName}
                        onChange={(event) =>
                          setNewMedicationName(event.target.value)
                        }
                        placeholder="Enter medication name"
                      />
                      <button
                        type="button"
                        onClick={createDraftMedicationFromMedsModal}
                        disabled={isCreatingDraftMedication}
                        className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
                      >
                        {isCreatingDraftMedication ? "Saving..." : "Add"}
                      </button>
                    </div>
                  </div>
                )}
                <div className="max-h-72 space-y-2 overflow-y-auto rounded-xl border border-slate-200 p-3 sm:max-h-[22rem]">
                  {medicationOptions.length === 0 && (
                    <p className="text-sm text-gray-500">
                      No medications found.
                    </p>
                  )}
                  {medicationOptions.map((medication) => {
                    const isDraft =
                      String(medication.status || "").toLowerCase() === "draft";
                    const isSelected = medDraft.medicationIds.includes(
                      medication.id,
                    );
                    return (
                      <div
                        key={medication.id}
                        className="rounded-lg border border-slate-200 bg-white p-2.5"
                      >
                        <label className="flex items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleMedSelection(medication.id)}
                          />
                          <span className="flex-1">
                            <span className="font-medium text-slate-800">
                              {medication.medicationName}
                            </span>{" "}
                            {isDraft && (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                Draft
                              </span>
                            )}
                            {(medication.dosage || medication.strength) && (
                              <span className="block text-xs text-slate-500">
                                Default:{" "}
                                {medication.dosage || medication.strength}
                              </span>
                            )}
                            {medication.frequency && (
                              <span className="block text-xs text-slate-500">
                                Frequency: {medication.frequency}
                              </span>
                            )}
                          </span>
                        </label>
                        {isSelected && (
                          <div className="mt-3 space-y-3">
                            <div>
                              <p className="mb-1 block text-xs font-medium text-slate-600">
                                Outcome
                              </p>
                              <div className="grid grid-cols-3 gap-2">
                                {Object.values(MEDICATION_OUTCOMES).map(
                                  (status) => {
                                    const outcome = getMedicationOutcome(
                                      medDraft.medicationOutcomes,
                                      medication.id,
                                    );
                                    const isActive =
                                      outcome.status === status ||
                                      (!outcome.status &&
                                        status === MEDICATION_OUTCOMES.GIVEN);
                                    return (
                                      <button
                                        key={status}
                                        type="button"
                                        onClick={() =>
                                          handleMedicationOutcomeChange(
                                            medication.id,
                                            status,
                                          )
                                        }
                                        className={`rounded-xl border px-2 py-2 text-xs font-semibold transition ${
                                          isActive
                                            ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                                        }`}
                                      >
                                        {status}
                                      </button>
                                    );
                                  },
                                )}
                              </div>
                            </div>

                            {isMedicationGiven(
                              getMedicationOutcome(
                                medDraft.medicationOutcomes,
                                medication.id,
                              ),
                            ) ? (
                              <div>
                                <label className="mb-1 block text-xs font-medium text-slate-600">
                                  Dosage administered
                                </label>
                                <input
                                  type="text"
                                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                                  value={
                                    medDraft.medicationDosages?.[
                                      medication.id
                                    ] || ""
                                  }
                                  onChange={(event) =>
                                    handleMedDosageChange(
                                      medication.id,
                                      event.target.value,
                                    )
                                  }
                                  placeholder="e.g., 1 tablet"
                                />
                              </div>
                            ) : (
                              <div>
                                <label className="mb-1 block text-xs font-medium text-slate-600">
                                  Reason
                                </label>
                                <select
                                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                                  value={
                                    getMedicationOutcome(
                                      medDraft.medicationOutcomes,
                                      medication.id,
                                    ).reason || ""
                                  }
                                  onChange={(event) =>
                                    handleMedicationOutcomeReasonChange(
                                      medication.id,
                                      event.target.value,
                                    )
                                  }
                                >
                                  <option value="">Select reason</option>
                                  {MEDICATION_HOLD_REASONS.map((reason) => (
                                    <option key={reason} value={reason}>
                                      {reason}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">
                    Event note (optional)
                  </label>
                  <textarea
                    rows={3}
                    className="w-full min-h-24 resize-y rounded-xl border border-slate-200 px-3 py-2"
                    value={medDraft.note}
                    onChange={(event) =>
                      setMedDraft((prev) => ({
                        ...prev,
                        note: event.target.value,
                      }))
                    }
                    placeholder="Taken after HD session"
                  />
                </div>
              </div>
            )}

            {activeModal === "note" && (
              <div>
                <label className="text-xs text-gray-600 block mb-1">
                  Notes
                </label>
                <textarea
                  rows={5}
                  className="w-full min-h-28 resize-y rounded-xl border border-slate-200 px-3 py-2"
                  value={noteDraft}
                  onChange={(event) => setNoteDraft(event.target.value)}
                  placeholder="Patient seemed weak. Access opened at 2:08 PM."
                />
              </div>
            )}
            {activeModal === "incident" && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-600 block mb-1">
                    Incident Type
                  </label>
                  <select
                    value={incidentDraft.type}
                    onChange={(event) =>
                      setIncidentDraft((prev) => ({
                        ...prev,
                        type: event.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-slate-200 px-3 py-2"
                  >
                    <option>Fall</option>
                    <option>Missed Medication</option>
                    <option>Adverse Event</option>
                    <option>Behavioral</option>
                    <option>Equipment Malfunction</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">
                    Description
                  </label>
                  <textarea
                    rows={4}
                    className="w-full min-h-24 resize-y rounded-xl border border-slate-200 px-3 py-2"
                    value={incidentDraft.description}
                    onChange={(event) =>
                      setIncidentDraft((prev) => ({
                        ...prev,
                        description: event.target.value,
                      }))
                    }
                    placeholder="Describe what happened."
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">
                    Action Taken
                  </label>
                  <textarea
                    rows={3}
                    className="w-full min-h-24 resize-y rounded-xl border border-slate-200 px-3 py-2"
                    value={incidentDraft.actionTaken}
                    onChange={(event) =>
                      setIncidentDraft((prev) => ({
                        ...prev,
                        actionTaken: event.target.value,
                      }))
                    }
                    placeholder="Document actions done immediately after the incident."
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2.5">
              <button
                type="button"
                onPointerDown={blurActiveField}
                onClick={closeModal}
                className="min-h-11 rounded-xl border border-slate-300 px-4 py-2 font-medium text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              {activeModal === "vitals" && (
                <button
                  type="button"
                  onPointerDown={blurActiveField}
                  onClick={saveVitalsSegment}
                  className="min-h-11 rounded-xl bg-blue-600 px-4 py-2 font-semibold text-white transition hover:bg-blue-700"
                >
                  {editingSegmentId ? "Update" : "Insert"}
                </button>
              )}
              {activeModal === "meds" && (
                <button
                  type="button"
                  onClick={saveMedsSegment}
                  className="min-h-11 rounded-xl bg-emerald-600 px-4 py-2 font-semibold text-white transition hover:bg-emerald-700"
                >
                  {editingSegmentId ? "Update" : "Insert"}
                </button>
              )}
              {activeModal === "note" && (
                <button
                  type="button"
                  onClick={saveNoteSegment}
                  className="min-h-11 rounded-xl bg-violet-600 px-4 py-2 font-semibold text-white transition hover:bg-violet-700"
                >
                  {editingSegmentId ? "Update" : "Insert"}
                </button>
              )}
              {activeModal === "incident" && (
                <button
                  type="button"
                  onClick={saveIncidentSegment}
                  className="min-h-11 rounded-xl bg-orange-600 px-4 py-2 font-semibold text-white transition hover:bg-orange-700"
                >
                  {editingSegmentId ? "Update" : "Insert"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 shadow-lg">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default DailyCareLogPage;
